import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistConfig } from './persistConfig';

export interface CartItem {
  id: string;
  brand_name: string;
  price: number;
  quantity: number;
  image: string | null;
  category: string | null;
  availableQuantity: number;
}

interface CartState {
  items: CartItem[];
}

interface CartActions {
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getSubtotal: () => number;
  getItem: (id: string) => CartItem | undefined;
  isPrescriptionRequired: () => boolean;
}

interface CartUIState {
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

type CartStore = CartState & CartActions & CartUIState;

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: item =>
        set(state => {
          const existing = state.items.find(i => i.id === item.id);

          if (existing) {
            const newQuantity = existing.quantity + 1;
            if (newQuantity > item.availableQuantity) {
              return state;
            }
            return {
              items: state.items.map(i =>
                i.id === item.id ? { ...i, quantity: newQuantity } : i
              ),
            };
          }

          return {
            items: [...state.items, { ...item, quantity: 1 }],
          };
        }),

      removeItem: id =>
        set(state => ({
          items: state.items.filter(item => item.id !== id),
        })),

      updateQuantity: (id, quantity) =>
        set(state => {
          if (quantity <= 0) {
            return { items: state.items.filter(item => item.id !== id) };
          }

          return {
            items: state.items.map(item => {
              if (item.id !== id) return item;
              return {
                ...item,
                quantity: Math.min(quantity, item.availableQuantity),
              };
            }),
          };
        }),

      clearCart: () => set({ items: [] }),

      openCart: () => set({ isOpen: true }),

      closeCart: () => set({ isOpen: false }),

      getSubtotal: () => {
        const { items } = get();
        return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      getItem: id => {
        const { items } = get();
        return items.find(item => item.id === id);
      },

      isPrescriptionRequired: () => {
        const { items } = get();
        return items.some(item => item.category?.toUpperCase() === 'POM');
      },
    }),
    {
      ...persistConfig('medication-tray-storage'),
      partialize: state => ({ items: state.items }),
    }
  )
);

export const isPOMCategory = (category: string | null | undefined): boolean =>
  category?.toUpperCase() === 'POM';
